/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SelectField, SelectFieldOption } from '@squiz/sds';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { SEGMENT_BUILDER_DATA_IDS, SEGMENT_BUILDER_FORM_SELECT_IDS } from '../../SegmentBuilder.constants';
import { RULE_OPTIONS, RULE_OPTIONS_NUMERIC, RULE_TYPE_MAP } from './SelectSegmentRule.constants';
import { RuleTypeProps, SELECT_TYPE_NUMERIC, SelectSegmentRuleProps } from './SelectSegmentRule.props';

/**
 * The `rule` select field.
 * @param {SelectSegmentRuleProps} props Props passed to `<SelectSegmentRule />`.
 * @returns {JSX.Element} The `<SelectSegmentRule />` component.
 */
export const SelectSegmentRule = (props: SelectSegmentRuleProps): JSX.Element => {
  const { dataType, errors = {}, onChange, preselectedRule, ruleId } = props;
  const { t } = useTranslation();

  const ruleRef = useRef<HTMLInputElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      inputLabel: t(CDP_STRINGS.ruleSelectLabel),
      placeholderText: t(CDP_STRINGS.ruleSelectPlaceholder),
    };
  }, [t]);

  // Change select options depending on data types.
  let selectOptions = RULE_OPTIONS;
  if (dataType === SELECT_TYPE_NUMERIC) {
    selectOptions = RULE_OPTIONS_NUMERIC;
  }

  // Use pre-selected rule as default if it's provided.
  let defaultSelection: RuleTypeProps | undefined = undefined;
  let defaultOption = undefined;

  if (preselectedRule) {
    defaultSelection = preselectedRule as RuleTypeProps;
    const defaultSelectionLabel = RULE_TYPE_MAP.get(defaultSelection ? defaultSelection : '');
    defaultOption = {
      key: defaultSelection,
      label: defaultSelectionLabel,
      value: defaultSelection,
    } as SelectFieldOption;
  }

  const [selectedRule, setSelectedRule] = useState<RuleTypeProps>(
    !isUndefined(defaultSelection) ? defaultSelection : ``,
  );

  const handleRuleChange = (option: SelectFieldOption): void => {
    setSelectedRule(option.value as RuleTypeProps);
    if (onChange) {
      onChange(ruleRef, option.value);
    }
  };

  return (
    <>
      <SelectField
        callback={handleRuleChange}
        data-id={SEGMENT_BUILDER_DATA_IDS.segmentComparisonSelect}
        defaultOption={defaultOption}
        errorMessage={
          property(`${SEGMENT_BUILDER_FORM_SELECT_IDS.comparison}-${ruleId}`)(errors) as string | null | undefined
        }
        fieldLabel={labels.inputLabel}
        id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.comparison}-${ruleId}`}
        name={SEGMENT_BUILDER_FORM_SELECT_IDS.comparison}
        options={selectOptions}
        placeholder={labels.placeholderText}
        ref={ruleRef}
        required
        value={selectedRule}
      />
    </>
  );
};
